import { BlockBlobClient } from '@azure/storage-blob';
import axios from 'axios';
import { type AdminRole, type AdminStatus } from './constants/admin';
import { loadingStore } from './loadingStore';
import { message } from 'antd';
import { UNAUTHORIZED_MESSAGES } from '../enum/unauthorize';

const baseURL = process.env.REACT_APP_API_URL || 'http://localhost:4000';
let isHandlingUnauthorized = false;
const DEFAULT_UPLOAD_INACTIVITY_TIMEOUT_MS = 120 * 1000;
const DEFAULT_UPLOAD_OVERALL_TIMEOUT_MS = 10 * 60 * 1000;

type LoadingAwareConfig = {
  skipGlobalLoading?: boolean;
};

function shouldSkipGlobalLoading(config?: unknown) {
  return Boolean((config as LoadingAwareConfig | undefined)?.skipGlobalLoading);
}

export class BlobUploadTimeoutError extends Error {
  code: 'UPLOAD_INACTIVITY_TIMEOUT' | 'UPLOAD_OVERALL_TIMEOUT';

  constructor(
    code: 'UPLOAD_INACTIVITY_TIMEOUT' | 'UPLOAD_OVERALL_TIMEOUT',
    message: string,
  ) {
    super(message);
    this.name = 'BlobUploadTimeoutError';
    this.code = code;
  }
}

export function isBlobUploadTimeoutError(
  error: unknown,
): error is BlobUploadTimeoutError {
  return error instanceof BlobUploadTimeoutError;
}

// Admin API instance - uses Authorization header, redirects to /admin/login on 401
export const adminApi = axios.create({
  baseURL,
  withCredentials: false,
  headers: { 'Content-Type': 'application/json' }
});

// App API instance - uses X-Session-Token header, redirects to /login on 401
export const appApi = axios.create({
  baseURL,
  withCredentials: false,
  headers: { 'Content-Type': 'application/json' }
});

// Legacy api instance for backward compatibility (defaults to admin behavior)
export const api = adminApi;

// Public API instance without 401 auto-logout (useful for login endpoints to surface errors)
export const publicApi = axios.create({
  baseURL,
  withCredentials: false,
  headers: { 'Content-Type': 'application/json' }
});

export function logout() {
  try {
    localStorage.removeItem('admin-auth-token');
    localStorage.removeItem('admin-auth-expiry');
  } catch {}
  if (typeof window !== 'undefined') {
    window.location.href = '/admin/login';
  }
}

export function logoutSession() {
  try {
    sessionStorage.removeItem('session-jwt-token');
  } catch {}
  if (typeof window !== 'undefined') {
    window.location.href = '/login';
  }
}

// Admin API: Attach Authorization header if token exists
adminApi.interceptors.request.use((config) => {
  // increment global loading count
  if (!shouldSkipGlobalLoading(config)) {
    loadingStore.increment();
  }
  try {
    const adminToken = localStorage.getItem('admin-auth-token');
    if (adminToken) {
      config.headers = config.headers || {};
      (config.headers as any)['Authorization'] = `Bearer ${adminToken}`;
    }
  } catch {}
  return config;
});

// App API: Attach X-Session-Token header if token exists
appApi.interceptors.request.use((config) => {
  // increment global loading count
  loadingStore.increment();
  try {
    const sessionToken = sessionStorage.getItem('session-jwt-token');
    if (sessionToken) {
      config.headers = config.headers || {};
      (config.headers as any)['X-Session-Token'] = sessionToken;
    }
  } catch {}
  return config;
});

// Admin API: Handle 401 to force admin login
adminApi.interceptors.response.use(
  (res) => {
    // decrement on success
    if (!shouldSkipGlobalLoading(res.config)) {
      loadingStore.decrement();
    }
    return res;
  },
  (err) => {
    // decrement on error
    if (!shouldSkipGlobalLoading(err?.config)) {
      loadingStore.decrement();
    }
    if (err?.response?.status === 401) {
      if (isHandlingUnauthorized) {
        return Promise.reject(err);
      }
      isHandlingUnauthorized = true;
      const error = err?.response?.data?.error ?? "DEFAULT";
      const msg = UNAUTHORIZED_MESSAGES[error] ?? UNAUTHORIZED_MESSAGES.DEFAULT;
      message.error(msg);
      logout();
    }
    return Promise.reject(err);
  }
);

// App API: Handle 401 - dispatch event to show login popup (app has no login page)
appApi.interceptors.response.use(
  (res) => {
    // decrement on success
    loadingStore.decrement();
    return res;
  },
  (err) => {
    // decrement on error
    loadingStore.decrement();
    if (err?.response?.status === 401) {
      if (isHandlingUnauthorized) {
        return Promise.reject(err);
      }
      isHandlingUnauthorized = true;
      // Dispatch event to show login popup instead of error message
      window.dispatchEvent(new CustomEvent('app-auth-required'));
      setTimeout(() => {
        isHandlingUnauthorized = false;
      }, 1000);
    }
    return Promise.reject(err);
  }
);

// publicApi should also affect loading (e.g., login)
publicApi.interceptors.request.use((config) => {
  loadingStore.increment();
  return config;
});

publicApi.interceptors.response.use(
  (res) => {
    loadingStore.decrement();
    return res;
  },
  (err) => {
    loadingStore.decrement();
    return Promise.reject(err);
  }
);

// Get a write-enabled SAS URL for a new blob based on the original filename
export async function getUploadSas(
  originalName: string,
  prefix?: string,
  signal?: AbortSignal,
): Promise<{ sasUrl: string; blobUrl: string; blobName: string; installerFileName: string }> {
  const res = await api.post(
    `/api/files/upload-sas`,
    { originalName, prefix },
    { signal },
  );
  return res.data as { sasUrl: string; blobUrl: string; blobName: string; installerFileName: string };
}

// Upload a file directly to Azure Blob Storage using SAS URL, optimized for large files
// Allows optional onProgress callback and AbortSignal for cancel support
export async function uploadDirectToBlob(
  file: File,
  prefix?: string,
  options?: {
    onProgress?: (ev: { loadedBytes: number }) => void;
    abortSignal?: AbortSignal;
    blockSizeMB?: number; // default 4MB
    concurrency?: number; // default 4
    inactivityTimeoutMs?: number; // default 120s without progress
    overallTimeoutMs?: number; // default 10 minutes
  }
): Promise<{ url: string; blobName: string; installerFileName: string }> {
  // Manually toggle loading since this path bypasses axios
  loadingStore.increment();
  const uploadAbortController = new AbortController();
  let timeoutError: BlobUploadTimeoutError | null = null;
  let inactivityTimer: ReturnType<typeof setTimeout> | null = null;
  let overallTimer: ReturnType<typeof setTimeout> | null = null;

  const clearInactivityTimer = () => {
    if (inactivityTimer) {
      clearTimeout(inactivityTimer);
      inactivityTimer = null;
    }
  };

  const clearOverallTimer = () => {
    if (overallTimer) {
      clearTimeout(overallTimer);
      overallTimer = null;
    }
  };

  const inactivityTimeoutMs = Math.max(
    1,
    options?.inactivityTimeoutMs ?? DEFAULT_UPLOAD_INACTIVITY_TIMEOUT_MS,
  );
  const overallTimeoutMs = Math.max(
    1,
    options?.overallTimeoutMs ?? DEFAULT_UPLOAD_OVERALL_TIMEOUT_MS,
  );

  const resetInactivityTimer = () => {
    clearInactivityTimer();
    inactivityTimer = setTimeout(() => {
      timeoutError = new BlobUploadTimeoutError(
        'UPLOAD_INACTIVITY_TIMEOUT',
        'Upload timed out after 120 seconds without progress.',
      );
      uploadAbortController.abort();
    }, inactivityTimeoutMs);
  };

  const handleExternalAbort = () => {
    uploadAbortController.abort();
  };

  if (options?.abortSignal) {
    if (options.abortSignal.aborted) {
      uploadAbortController.abort();
    } else {
      options.abortSignal.addEventListener('abort', handleExternalAbort, {
        once: true,
      });
    }
  }

  try {
    resetInactivityTimer();
    overallTimer = setTimeout(() => {
      timeoutError = new BlobUploadTimeoutError(
        'UPLOAD_OVERALL_TIMEOUT',
        'Upload timed out after 10 minutes.',
      );
      uploadAbortController.abort();
    }, overallTimeoutMs);

    const { sasUrl, blobUrl, blobName, installerFileName } = await getUploadSas(
      file.name,
      prefix,
      uploadAbortController.signal,
    );

    const contentType = file.type || 'application/octet-stream';
    const client = new BlockBlobClient(sasUrl);

    const blockSize = Math.max(1, Math.floor((options?.blockSizeMB ?? 4))) * 1024 * 1024; // MB to bytes
    const concurrency = Math.max(1, options?.concurrency ?? 4);

    await client.uploadData(file, {
      blockSize,
      concurrency,
      maxSingleShotSize: blockSize, 
      abortSignal: uploadAbortController.signal,
      onProgress: (ev) => {
        resetInactivityTimer();
        options?.onProgress?.(ev);
      },
      blobHTTPHeaders: { blobContentType: contentType },
    });

    return { url: blobUrl, blobName, installerFileName };
  } catch (error) {
    if (timeoutError) {
      throw timeoutError;
    }
    throw error;
  } finally {
    clearInactivityTimer();
    clearOverallTimer();
    options?.abortSignal?.removeEventListener('abort', handleExternalAbort);
    loadingStore.decrement();
  }
}

// ===== Admin Account APIs =====
export type AdminUser = { id: number; username: string; email?: string | null; role: AdminRole | undefined; status?: AdminStatus };

export async function getCurrentAdmin(): Promise<AdminUser> {
  const res = await api.get('/api/admin/auth/me');
  return res.data as AdminUser;
}

export async function updateAccountUsername(payload: { newUsername: string; password: string }): Promise<AdminUser> {
  const res = await api.put('/api/admin/auth/account/username', payload);
  return res.data as AdminUser;
}

export async function changeAccountPassword(payload: { oldPassword: string; newPassword: string }): Promise<{ success: boolean }> {
  const res = await api.put('/api/admin/auth/account/password', payload);
  return res.data as { success: boolean };
}

// ===== Password Reset APIs =====
export async function requestPasswordReset(email: string): Promise<{ success: boolean }> {
  const res = await api.post('/api/admin/auth/password/reset/request', { email });
  return res.data as { success: boolean };
}

export async function confirmPasswordReset(token: string, newPassword: string): Promise<{ success: boolean }> {
  const res = await api.post('/api/admin/auth/password/reset/confirm', { token, newPassword });
  return res.data as { success: boolean };
}

export async function validatePasswordResetToken(token: string): Promise<{ valid: boolean; error?: string }> {
  const res = await api.get('/api/admin/auth/password/reset/validate', { params: { token } });
  return res.data as { valid: boolean; error?: string };
}

// ===== Super Admin: Admin Users Management =====
export async function listAdminUsers(): Promise<AdminUser[]> {
  const res = await api.get('/api/admin/users');
  return res.data as AdminUser[];
}

export async function createSubAdmin(payload: { email: string; password: string; username?: string }): Promise<AdminUser> {
  const res = await api.post('/api/admin/users', payload);
  return res.data as AdminUser;
}

export async function deleteAdminUser(id: number): Promise<void> {
  await api.delete(`/api/admin/users/${id}`);
}

// Update admin user. If username (email) changes, backend sends verification email and marks status as 未確認 until verified.
export async function updateAdminUser(id: number, payload: { password?: string; username?: string }): Promise<AdminUser> {
  const res = await api.put(`/api/admin/users/${id}`, payload);
  return res.data as AdminUser;
}

// Confirm email change by token (public endpoint). Returns { success: boolean }
export async function confirmAdminEmailChange(token: string): Promise<{ success: boolean; error?: string }> {
  const res = await publicApi.get('/api/admin/users/verify-email', { params: { token } });
  return res.data as { success: boolean; error?: string };
}

// Resend verification email for an admin user (SUPER only)
export async function resendAdminVerification(id: number): Promise<{ success: boolean }> {
  const res = await api.post(`/api/admin/users/${id}/resend-verification`);
  return res.data as { success: boolean };
}
